/**
 * Static benefit rows for Clinician and Practice upgrade modals (trial-recap variant).
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10266-38745
 *
 * Import this module directly (`…/upgrade-trial-feature-list`). Do not re-export from
 * `trial-recap-modal/index.ts` — that barrel loads `TrialRecapModal` and its dependency graph.
 */

import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { LibraryBig } from '@heidi/design-system/icons';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { Info, MessageSquareText, TextSearch } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { clinicianTrialEndedModalMessages } from '../clinician-trial-ended-modal/clinician-trial-ended-modal-messages';
import { formatEvidenceUsageBadge } from '../trial-recap-modal/utils';
import { getCommerceTrialsUiPorts } from '../trials-ui-ports';
import { upgradeTrialFeatureListMessages } from './upgrade-trial-feature-list-messages';

export interface UpgradeTrialFeatureListProps {
  /** User's current paid plan tier (not the plan the upgrade modal is promoting). */
  tier: SubscriptionTier | undefined;
  /** @default 'upgrade-trial-feature-list' */
  testId?: string;
}

export function UpgradeTrialFeatureList({
  tier,
  testId = 'upgrade-trial-feature-list',
}: UpgradeTrialFeatureListProps) {
  const intl = useIntl();
  const {
    StackedSourceAvatars,
    useTrialRecapUsageStats,
    useQuerySourceRecommendations,
    useEvidenceAccess,
  } = getCommerceTrialsUiPorts();
  const { usageStats } = useTrialRecapUsageStats(true);
  const { data: recommendationsData } = useQuerySourceRecommendations();
  const sourceCollectionAvatars = useMemo(
    () =>
      (recommendationsData?.sources ?? []).slice(0, 3).map((s) => ({ domain: s.domain })),
    [recommendationsData?.sources]
  );

  const { isInSessionEvidenceShown } = useEvidenceAccess();
  const isClinicianPlan = tier === SubscriptionTier.CLINICIAN_PRO;
  const answersInPatientSessionsUsageCount = usageStats?.answers_from_evidence ?? 0;

  return (
    <div className="flex w-full flex-col gap-2" data-testid={testId}>
      <TypographyP2 className="text-text-secondary">
        <FormattedMessage
          {...upgradeTrialFeatureListMessages.everythingInCurrentPlanPlus}
        />
      </TypographyP2>
      <ul className="flex w-full flex-col">
        {isInSessionEvidenceShown && (
          <li className="flex items-center gap-2 border-b border-border px-2 py-4 last:border-b-0">
            <MessageSquareText
              aria-hidden
              className="size-5 shrink-0 text-text-primary"
            />
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <TypographyP className="min-w-0 text-text-primary">
                <FormattedMessage
                  {...upgradeTrialFeatureListMessages.featureAnswersInPatientSessions}
                />
              </TypographyP>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    iconOnly
                    className="size-6 shrink-0 text-text-secondary hover:text-text-primary"
                    aria-label={intl.formatMessage(
                      upgradeTrialFeatureListMessages.featureMoreEvidenceInPatientSessionsTooltipAria
                    )}
                  >
                    <Info aria-hidden />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-start">
                  <FormattedMessage
                    {...upgradeTrialFeatureListMessages.featureMoreEvidenceInPatientSessionsTooltip}
                  />
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </div>
            {answersInPatientSessionsUsageCount > 0 ? (
              <Badge
                color={isClinicianPlan ? 'proTier' : 'neutral'}
                size="sm"
                className={cn(
                  'shrink-0 shadow-none',
                  !isClinicianPlan && 'bg-tier-practice-muted text-tier-practice-text'
                )}
              >
                {formatEvidenceUsageBadge(
                  intl,
                  'usedCount',
                  answersInPatientSessionsUsageCount
                )}
              </Badge>
            ) : null}
          </li>
        )}
        <li className="flex items-center gap-2 border-b border-border px-2 py-4 last:border-b-0">
          <TextSearch aria-hidden className="size-5 shrink-0 text-text-primary" />
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <TypographyP className="min-w-0 text-text-primary">
              <FormattedMessage
                {...upgradeTrialFeatureListMessages.featurePreferredSources}
              />
            </TypographyP>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  className="size-6 shrink-0 text-text-secondary hover:text-text-primary"
                  aria-label={intl.formatMessage(
                    clinicianTrialEndedModalMessages.featureSetPreferredSourcesTooltipAriaLabel
                  )}
                >
                  <Info aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-start">
                <FormattedMessage
                  {...clinicianTrialEndedModalMessages.featureSetPreferredSourcesTooltip}
                />
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </div>
          {sourceCollectionAvatars.length > 0 ? (
            <div className="flex shrink-0 items-center">
              <StackedSourceAvatars
                sources={sourceCollectionAvatars}
                size="sm"
                maxVisible={3}
              />
            </div>
          ) : null}
        </li>

        {/* "Build an Evidence library" — added per Figma node 11262:37233.
            Always shown so UK / EEA users (who don't see the
            "Answers in patient sessions" row) still get two upgrade benefits
            in the list. */}
        <li className="flex items-center gap-2 border-b border-border px-2 py-4 last:border-b-0">
          <LibraryBig aria-hidden size={20} className="shrink-0 text-text-primary" />
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <TypographyP className="min-w-0 text-text-primary">
              <FormattedMessage
                {...upgradeTrialFeatureListMessages.featureBuildEvidenceLibrary}
              />
            </TypographyP>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  className="size-6 shrink-0 text-text-secondary hover:text-text-primary"
                  aria-label={intl.formatMessage(
                    upgradeTrialFeatureListMessages.featureBuildEvidenceLibraryTooltipAria
                  )}
                >
                  <Info aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-start">
                <FormattedMessage
                  {...upgradeTrialFeatureListMessages.featureBuildEvidenceLibraryTooltip}
                />
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </div>
        </li>
      </ul>
    </div>
  );
}
